import React, { useContext, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSortUp,
  faSortDown,
  faSort,
  faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons';
import Pagination from 'react-js-pagination';
import DashboardContext from '../../../../contexts/DashboardContext';
import Loading from '../../../Shared/ResourceIndex/Loading';
import { Link } from 'react-router-dom';
import { addDays, parse, isBefore, format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { AppDivisionContext } from '../../../../contexts/AppDivisionContext';

const DATE_FORMAT = 'MM-dd-yyyy';

const getUTCTime = (date: string) => {
  return toZonedTime(new Date(date), 'UTC');
};

const Permits = (props: any) => {
  const { appDivisionId } = useContext(AppDivisionContext);
  const {
    permitsList,
    isLoadingForPermits,
    setQueryParamsForPermits,
    queryParamsForPermits,
  } = useContext(DashboardContext);
  const [currOrderStatus, setCurrOrderStatus] = useState<any>({
    siteName: 'DESC',
    clientName: 'DESC',
    permitType: 'DESC',
    permitNumber: 'DESC',
    primaryContactName: 'DESC',
    expirationDate: 'DESC',
  });

  // icons holder
  const [siteNameIcon, setSiteNameIcon] = useState<any>(faSort);
  const [clientNameIcon, setClientNameIcon] = useState<any>(faSort);
  const [permitTypeIcon, setPermitTypeIcon] = useState<any>(faSort);
  const [permitNumberIcon, setPermitNumberIcon] = useState<any>(faSort);
  const [primaryContactNameIcon, setPrimaryContactNameIcon] =
    useState<any>(faSort);
  const [expirationDate, setExpirationDate] = useState<any>(faSort);

  const updateIcon = (iconType: string, order: string) => {
    setCurrOrderStatus((prevState: any) => ({
      ...prevState,
      [iconType]: order,
    }));
    switch (iconType) {
      case 'siteName':
        setSiteNameIcon(
          order === 'DESC' ? faSortDown : order === 'ASC' ? faSortUp : faSort,
        );
        setClientNameIcon(faSort);
        setPermitTypeIcon(faSort);
        setPermitNumberIcon(faSort);
        setPrimaryContactNameIcon(faSort);
        setExpirationDate(faSort);
        setQueryParamsForPermits({
          ...queryParamsForPermits,
          orderBy: 'projectName',
          orderType: order,
          p: 1,
        });
        break;
      case 'clientName':
        setClientNameIcon(
          order === 'DESC' ? faSortDown : order === 'ASC' ? faSortUp : faSort,
        );
        setSiteNameIcon(faSort);
        setPermitTypeIcon(faSort);
        setPermitNumberIcon(faSort);
        setPrimaryContactNameIcon(faSort);
        setExpirationDate(faSort);
        setQueryParamsForPermits({
          ...queryParamsForPermits,
          orderBy: 'clientName',
          orderType: order,
          p: 1,
        });
        break;
      case 'permitType':
        setPermitTypeIcon(
          order === 'DESC' ? faSortDown : order === 'ASC' ? faSortUp : faSort,
        );
        setSiteNameIcon(faSort);
        setClientNameIcon(faSort);
        setPermitNumberIcon(faSort);
        setPrimaryContactNameIcon(faSort);
        setExpirationDate(faSort);
        setQueryParamsForPermits({
          ...queryParamsForPermits,
          orderBy: 'permitType',
          orderType: order,
          p: 1,
        });
        break;
      case 'permitNumber':
        setPermitNumberIcon(
          order === 'DESC' ? faSortDown : order === 'ASC' ? faSortUp : faSort,
        );
        setSiteNameIcon(faSort);
        setClientNameIcon(faSort);
        setPermitTypeIcon(faSort);
        setPrimaryContactNameIcon(faSort);
        setExpirationDate(faSort);
        setQueryParamsForPermits({
          ...queryParamsForPermits,
          orderBy: 'permitNumber',
          orderType: order,
          p: 1,
        });
        break;
      case 'primaryContactName':
        setPrimaryContactNameIcon(
          order === 'DESC' ? faSortDown : order === 'ASC' ? faSortUp : faSort,
        );
        setSiteNameIcon(faSort);
        setClientNameIcon(faSort);
        setPermitTypeIcon(faSort);
        setPermitNumberIcon(faSort);
        setExpirationDate(faSort);
        setQueryParamsForPermits({
          ...queryParamsForPermits,
          orderBy: 'primaryContactName',
          orderType: order,
          p: 1,
        });
        break;
      case 'expirationDate':
        setExpirationDate(
          order === 'DESC' ? faSortDown : order === 'ASC' ? faSortUp : faSort,
        );
        setSiteNameIcon(faSort);
        setClientNameIcon(faSort);
        setPermitTypeIcon(faSort);
        setQueryParamsForPermits({
          ...queryParamsForPermits,
          orderBy: 'expirationDate',
          orderType: order,
          p: 1,
        });
        break;
      default:
        break;
    }
  };

  const getStatus = (date: Date | null | undefined) => {
    if (!date) {
      return 'No Expiration';
    }

    const today = parse(
      format(new Date(), DATE_FORMAT),
      DATE_FORMAT,
      new Date(),
    );
    const future = parse(
      format(addDays(new Date(), 46), DATE_FORMAT),
      DATE_FORMAT,
      new Date(),
    );
    const permit = parse(
      format(new Date(date), DATE_FORMAT),
      DATE_FORMAT,
      new Date(),
    );
    
    if (isBefore(permit, today)) {
      return 'Expired';
    } else if (!isBefore(permit, today) && isBefore(permit, future)) {
      return 'Soon';
    } else {
      return 'Ok';
    }
  };

  return (
    <section className='permits-widget-section'>
      <header className='permits-widget-section__header'>
        <h2 className='permits-widget-section__title'>Permits</h2>
        <div className='permits-widget-section__controls'>
          <input
            type='text'
            placeholder='Search'
            value={queryParamsForPermits.s || ''}
            onChange={(e) =>
              setQueryParamsForPermits({
                ...queryParamsForPermits,
                s: e.target.value,
                p: 1,
              })
            }
          />
        </div>
      </header>
      <div
        data-rbd-drag-handle-context-id={props.providedProps}
        data-rbd-drag-handle-draggable-id='gibberish'
        style={{
          cursor: 'auto',
        }}
      >
        <div className='permits-widget-section__body'>
          <div className='permits-widget-section__content'>
            <section>
              <div className='permits-widget-section__table-head'>
                <h3
                  onClick={() =>
                    updateIcon(
                      'siteName',
                      currOrderStatus.siteName === 'DESC' ? 'ASC' : 'DESC',
                    )
                  }
                >
                  Site Name{' '}
                  <span>
                    <FontAwesomeIcon icon={siteNameIcon} />
                  </span>
                </h3>
                <h3
                  onClick={() =>
                    updateIcon(
                      'clientName',
                      currOrderStatus.clientName === 'DESC' ? 'ASC' : 'DESC',
                    )
                  }
                >
                  Client Name{' '}
                  <span>
                    <FontAwesomeIcon icon={clientNameIcon} />
                  </span>
                </h3>
                <h3
                  onClick={() =>
                    updateIcon(
                      'permitType',
                      currOrderStatus.permitType === 'DESC' ? 'ASC' : 'DESC',
                    )
                  }
                >
                  Permit Type{' '}
                  <span>
                    <FontAwesomeIcon icon={permitTypeIcon} />
                  </span>
                </h3>
                <h3
                  onClick={() =>
                    updateIcon(
                      'permitNumber',
                      currOrderStatus.permitNumber === 'DESC' ? 'ASC' : 'DESC',
                    )
                  }
                >
                  Permit Number{' '}
                  <span>
                    <FontAwesomeIcon icon={permitNumberIcon} />
                  </span>
                </h3>
                <h3
                  onClick={() =>
                    updateIcon(
                      'primaryContactName',
                      currOrderStatus.primaryContactName === 'DESC'
                        ? 'ASC'
                        : 'DESC',
                    )
                  }
                >
                  Contact{' '}
                  <span>
                    <FontAwesomeIcon icon={primaryContactNameIcon} />
                  </span>
                </h3>
                <h3
                  onClick={() =>
                    updateIcon(
                      'expirationDate',
                      currOrderStatus.expirationDate === 'DESC'
                        ? 'ASC'
                        : 'DESC',
                    )
                  }
                >
                  Expiration Date{' '}
                  <span>
                    <FontAwesomeIcon icon={expirationDate} />
                  </span>
                </h3>
                <h3>Action </h3>
              </div>
              {!isLoadingForPermits ? (
                permitsList && permitsList.count > 0 ? (
                  permitsList.rows.map((permit: any, index: number) => (
                    <div
                      key={index}
                      className='permits-widget-section__table-body'
                    >
                      <li title={permit.projectName}>{permit.projectName}</li>
                      <li title={permit.clientName}>{permit.clientName}</li>
                      <li title={permit.permitType}>{permit.permitType}</li>
                      <li title={permit.permitNumber}>{permit.permitNumber}</li>
                      <li
                        title={
                          permit.primaryContactName +
                          ' ' +
                          permit.primaryContactPhone
                        }
                      >
                        {permit.primaryContactName}
                        <br />
                        {permit.primaryContactPhone}
                      </li>
                      <li
                        style={{
                          color: permit.expirationDate
                            ? getStatus(getUTCTime(permit.expirationDate)) ===
                              'Expired'
                              ? '#FF4C51'
                              : getStatus(getUTCTime(permit.expirationDate)) ===
                                'Soon'
                              ? '#FF9F43'
                              : '#002230'
                            : '#002230',
                        }}
                      >
                        {permit.expirationDate
                          ? format(
                              getUTCTime(permit.expirationDate),
                              DATE_FORMAT,
                            )
                          : 'No Expiration'}
                      </li>
                      <li>
                        <Link
                          to={`/divisions/${appDivisionId}/projects/${permit.projectId}/project-docs`}
                        >
                          <span
                            className={`
                              ${
                                permit.expirationDate
                                  ? getStatus(
                                      getUTCTime(permit.expirationDate),
                                    ) === 'Expired'
                                    ? 'bg-danger'
                                    : getStatus(
                                        getUTCTime(permit.expirationDate),
                                      ) === 'Soon'
                                    ? 'bg-warning'
                                    : 'bg-primary'
                                  : 'bg-primary'
                              }
                              `}
                          >
                            View Permit
                          </span>
                        </Link>
                      </li>
                    </div>
                  ))
                ) : (
                  <div className='permits-widget-section__no-data'>
                    No Permits Found
                  </div>
                )
              ) : (
                <Loading what='data' />
              )}
            </section>
          </div>
          <footer className='permits-widget-section__footer'>
            <div className='permits-widget-section__pagination-wrapper'>
              <Pagination
                activePage={queryParamsForPermits.p || 1}
                itemsCountPerPage={10}
                totalItemsCount={Number(permitsList.count) || 1}
                pageRangeDisplayed={2}
                onChange={(currentPage) => {
                  setQueryParamsForPermits({
                    ...queryParamsForPermits,
                    p: currentPage,
                  });
                }}
                itemClass='pagination-item'
              />
            </div>
            <span onClick={props.changeShowMore}>
              Show {props.mdSize === 6 ? 'More' : 'Less'}{' '}
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </span>
          </footer>
        </div>
      </div>
    </section>
  );
};

export default Permits;
